import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  ExternalLink,
  Share2,
  Mail,
  Facebook,
  Copy,
  Check,
  ChevronDown,
  Coffee,
  Heart,
  Users,
  Telescope,
  MessageSquare,
  Waves,
  Sun,
  Landmark,
  MapPin,
} from 'lucide-react';
import Header from '../components/Header';

const links = {
  familyFund: 'https://www.mhdfamilyfund.org/',
  familyFundAbout: 'https://www.mhdfamilyfund.org/about',
  gatchellDonation: 'https://mhdrec.wufoo.com/forms/z1ir7nqq0xvfy9b/',
  bondCoffee: '/',
  instagram: 'https://www.instagram.com/marbleheadfamilyfund/',
  buyRoast: 'https://buy.stripe.com/bJecN6gubbdd9815VE3VC0t',
};

const projectCards = [
  {
    name: 'Hobbs Playground',
    description:
      'Rebuilt and completed in 2022, creating a more inclusive playground where differences disappear and children share experiences with their peers.',
    icon: 'playground',
    gradient: 'from-[#f3a06f]/30 to-[#c97b4a]/20',
    tag: 'Completed 2022',
  },
  {
    name: 'Gerry Playground at Stramski Way',
    description:
      'A neighborhood favorite, also home to one of the new communication boards installed in 2024.',
    icon: 'message',
    gradient: 'from-blue-500/30 to-blue-700/20',
    tag: 'Playground',
  },
  {
    name: 'Devereux Beach',
    description:
      'Improvements to the playground at one of Marblehead’s most loved waterfront spots.',
    icon: 'waves',
    gradient: 'from-cyan-500/30 to-blue-600/20',
    tag: 'Waterfront',
  },
  {
    name: 'Bud Orne Playground',
    description:
      'Another local playground supported by the Family Fund’s work around town.',
    icon: 'playground',
    gradient: 'from-[#f3a06f]/30 to-[#c97b4a]/20',
    tag: 'Playground',
  },
  {
    name: 'Seaside',
    description:
      'Upgrades to the Seaside playground, keeping it safe and inviting for Marblehead families.',
    icon: 'sun',
    gradient: 'from-amber-400/30 to-orange-600/20',
    tag: 'Playground',
  },
  {
    name: 'Chandler Hovey Park',
    description:
      'Home to the lighthouse point and the telescope the Family Fund helped bring to the park.',
    icon: 'telescope',
    gradient: 'from-emerald-500/30 to-teal-700/20',
    tag: 'Telescope',
  },
  {
    name: 'Fort Sewall',
    description:
      'Historic fort with harbor views, where the Family Fund supported the spyglass installation.',
    icon: 'fort',
    gradient: 'from-stone-500/30 to-stone-700/20',
    tag: 'Spyglass',
  },
];

function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

function Reveal({
  children,
  delay = 0,
  className = '',
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const { ref, visible } = useReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function ExternalLinkButton({
  href,
  children,
  variant = 'primary',
  className = '',
}: {
  href: string;
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  className?: string;
}) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f3a06f] focus-visible:ring-offset-2 focus-visible:ring-offset-[#132947]';
  const styles = {
    primary:
      'bg-[#f3a06f] text-[#132947] px-8 py-4 text-lg hover:bg-[#e88f5a] hover:scale-105 shadow-lg shadow-[#f3a06f]/20',
    secondary:
      'bg-white/10 text-white border border-white/20 px-8 py-4 text-lg hover:bg-white/20 backdrop-blur-md',
    ghost:
      'text-[#f3a06f] hover:text-[#e88f5a] px-2 py-1 text-base underline-offset-4 hover:underline',
  } as const;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${base} ${styles[variant]} ${className}`}
    >
      {children}
      <ExternalLink className="w-4 h-4 opacity-70" aria-hidden="true" />
    </a>
  );
}

export default function MFFImpact() {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const shareData = {
      title: 'Coffee & Community | Bond Coffee × Marblehead Family Fund',
      text: 'See how your Bond Coffee purchase supports the Marblehead Family Fund and helps create safe, inclusive spaces for Marblehead families.',
      url: window.location.href,
    };
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        /* user dismissed */
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        /* clipboard blocked */
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#132947] text-white">
      <Header />

      {/* 1. HERO */}
      <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#132947] via-[#1a3a5e] to-[#0f2238]">
        <div className="absolute inset-0 opacity-30" aria-hidden="true">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#f3a06f]/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-700/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-white/5 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] border border-white/10 rounded-full" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#132947]/40 to-[#132947]" />

        <div className="relative z-10 px-6 max-w-2xl mx-auto text-center pt-24 pb-32">
          <Reveal>
            <p className="text-[#f3a06f] text-xs sm:text-sm font-bold uppercase tracking-[0.25em] mb-6">
              Bond Coffee × Marblehead Family Fund
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl font-bold leading-[1.05] tracking-tight mb-6">
              Coffee &amp; Community.
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-xl sm:text-2xl text-white/90 font-light mb-4 max-w-xl mx-auto">
              A local roast helping build better places for Marblehead families.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <p className="text-base sm:text-lg text-[#f3a06f]/90 font-medium">
              Scan. Sip. See where your coffee is making an impact.
            </p>
          </Reveal>
        </div>

        <button
          onClick={() =>
            document
              .getElementById('gives-back')
              ?.scrollIntoView({ behavior: 'smooth' })
          }
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60 hover:text-white transition-colors"
          aria-label="Scroll to learn more"
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <ChevronDown className="w-5 h-5 animate-bounce" />
        </button>
      </section>

      {/* 2. YOUR BAG GIVES BACK */}
      <section
        id="gives-back"
        className="bg-[#f5eddf] text-[#132947] py-20 sm:py-28 px-6"
      >
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="text-[#c97b4a] text-xs sm:text-sm font-bold uppercase tracking-[0.25em] mb-4">
              Your Coffee Did Something Good
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold mb-12">
              This bag gives back.
            </h2>
          </Reveal>

          <Reveal delay={200}>
            <div className="relative inline-block mb-10">
              <div className="absolute -inset-6 bg-[#f3a06f]/20 rounded-full blur-2xl" />
              <div className="relative bg-white rounded-3xl shadow-xl px-10 py-12 sm:px-16 sm:py-16">
                <p className="font-serif text-7xl sm:text-8xl font-bold text-[#c97b4a] leading-none">
                  $10
                </p>
                <p className="text-lg sm:text-xl text-[#132947]/70 mt-4 max-w-xs mx-auto">
                  from every bag goes to the Marblehead Family Fund.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={300}>
            <p className="text-lg sm:text-xl text-[#132947]/80 leading-relaxed max-w-2xl mx-auto">
              Bond Coffee created this limited-release roast to help support the
              Marblehead Family Fund and the local spaces where Marblehead kids
              play, gather and grow.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 3. MEET THE MARBLEHEAD FAMILY FUND */}
      <section className="bg-[#132947] py-20 sm:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <p className="text-[#f3a06f] text-xs sm:text-sm font-bold uppercase tracking-[0.25em] mb-4 text-center">
              Meet the Family Fund
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-center mb-10">
              Built by Marblehead families, for Marblehead families.
            </h2>
          </Reveal>

          <Reveal delay={200}>
            <div className="space-y-6 text-lg text-white/80 leading-relaxed max-w-2xl mx-auto">
              <p>
                The Marblehead Family Fund was established in{' '}
                <strong className="text-white">1998</strong> by a group of local
                parents who wanted better recreational spaces for their children.
              </p>
              <p>
                Since then, the all-volunteer organization has worked to improve
                Marblehead’s playgrounds, parks, and open spaces — and
                to bring low-cost, family-focused events to the town’s
                recreational spaces.
              </p>
            </div>
          </Reveal>

          <Reveal delay={300}>
            <div className="flex flex-wrap justify-center gap-3 mt-10">
              {[
                { icon: Users, label: 'Playgrounds' },
                { icon: MapPin, label: 'Parks & Open Spaces' },
                { icon: Heart, label: 'Family Events' },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2.5"
                >
                  <Icon className="w-4 h-4 text-[#f3a06f]" />
                  <span className="text-sm text-white/80 font-medium">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={400}>
            <div className="text-center mt-12">
              <ExternalLinkButton href={links.familyFund} variant="primary">
                Visit the Family Fund
              </ExternalLinkButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4. LOOK AROUND TOWN */}
      <section className="bg-[#0f2238] py-20 sm:py-28 px-6">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-center mb-4">
              Their work is all around Marblehead.
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-lg text-white/70 text-center mb-12 max-w-xl mx-auto">
              You may already know some of the places the Family Fund has helped
              support.
            </p>
          </Reveal>

          {/* Horizontal swipeable cards */}
          <div className="flex gap-5 overflow-x-auto pb-6 snap-x snap-mandatory -mx-6 px-6 scrollbar-hide">
            {projectCards.map((card, i) => (
              <Reveal key={card.name} delay={i * 60}>
                <article className="snap-start shrink-0 w-72 sm:w-80 bg-[#132947] rounded-2xl overflow-hidden border border-white/10 shadow-lg transition-transform duration-300 hover:-translate-y-1.5 hover:shadow-2xl group">
                  <div className={`relative h-44 overflow-hidden bg-gradient-to-br ${card.gradient} flex items-center justify-center`}>
                    <span className="text-white/90">
                      {card.icon === 'playground' && <Users className="w-16 h-16" strokeWidth={1.2} />}
                      {card.icon === 'message' && <MessageSquare className="w-16 h-16" strokeWidth={1.2} />}
                      {card.icon === 'waves' && <Waves className="w-16 h-16" strokeWidth={1.2} />}
                      {card.icon === 'sun' && <Sun className="w-16 h-16" strokeWidth={1.2} />}
                      {card.icon === 'telescope' && <Telescope className="w-16 h-16" strokeWidth={1.2} />}
                      {card.icon === 'fort' && <Landmark className="w-16 h-16" strokeWidth={1.2} />}
                    </span>
                    <span className="absolute top-3 left-3 bg-[#132947]/80 backdrop-blur-sm text-[#f3a06f] text-xs font-semibold px-3 py-1 rounded-full">
                      {card.tag}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl font-bold mb-2">
                      {card.name}
                    </h3>
                    <p className="text-sm text-white/70 leading-relaxed">
                      {card.description}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          <p className="text-center text-white/40 text-sm mt-4 sm:hidden">
            Swipe to explore →
          </p>
        </div>
      </section>

      {/* 5. COMMUNICATION BOARDS */}
      <section className="bg-[#f5eddf] text-[#132947] py-20 sm:py-28 px-6">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="text-[#c97b4a] text-xs sm:text-sm font-bold uppercase tracking-[0.25em] mb-4 text-center">
              Making Play More Inclusive
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-center mb-10">
              Helping every kid communicate.
            </h2>
          </Reveal>

          <Reveal delay={200}>
            <div className="grid md:grid-cols-2 gap-8 items-center bg-white rounded-3xl shadow-xl overflow-hidden">
              <div className="relative h-64 md:h-full min-h-[280px] bg-gradient-to-br from-[#f3a06f]/30 via-[#c97b4a]/20 to-[#132947]/10 flex items-center justify-center">
                <div className="text-center px-8">
                  <MessageSquare className="w-20 h-20 text-[#c97b4a] mx-auto mb-4" strokeWidth={1.2} />
                  <div className="flex flex-wrap justify-center gap-2 max-w-xs mx-auto">
                    {['PLAY', 'EAT', 'DRINK', 'FRIEND', 'PAUSE', 'AGAIN'].map((word) => (
                      <span key={word} className="bg-[#132947]/10 text-[#132947]/70 text-xs font-bold px-3 py-1.5 rounded-lg border border-[#132947]/10">
                        {word}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="p-8 md:p-10">
                <MessageSquare className="w-10 h-10 text-[#c97b4a] mb-4" />
                <p className="text-lg leading-relaxed text-[#132947]/80 mb-6">
                  In <strong>2024</strong>, the Marblehead Family Fund partnered
                  with the Town of Marblehead Recreation &amp; Parks Department
                  and Power of Speech to help fundraise, design and install
                  communication boards at local playgrounds.
                </p>
                <p className="text-base text-[#132947]/60 italic">
                  The boards give non-verbal and early-communicating children a
                  way to express themselves at play.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. GATCHELL — MAIN FEATURE */}
      <section className="relative bg-[#132947] py-24 sm:py-32 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-20" aria-hidden="true">
          <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-[#f3a06f]/15 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#132947] via-[#132947]/90 to-[#132947]" />

        <div className="relative max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="text-[#f3a06f] text-xs sm:text-sm font-bold uppercase tracking-[0.25em] mb-4">
              What’s Next
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-serif text-5xl sm:text-6xl font-bold mb-8">
              Give for Gatchell.
            </h2>
          </Reveal>

          <Reveal delay={200}>
            <div className="relative rounded-3xl mb-10 shadow-2xl border border-white/10 bg-gradient-to-br from-[#1a3a5e] via-[#132947] to-[#0f2238] h-64 sm:h-80 flex items-center justify-center overflow-hidden">
              <div className="absolute inset-0 opacity-10" aria-hidden="true">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border border-white/20 rounded-full" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 border border-white/10 rounded-full" />
              </div>
              <div className="relative text-center">
                <Users className="w-16 h-16 text-[#f3a06f] mx-auto mb-3" strokeWidth={1.2} />
                <p className="text-white/90 text-sm font-medium">
                  Gatchell Playground, Marblehead
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={300}>
            <p className="text-xl sm:text-2xl text-white/90 leading-relaxed mb-6 max-w-2xl mx-auto">
              The Marblehead Family Fund’s next major project is the redesign
              of Gatchell Playground.
            </p>
          </Reveal>
          <Reveal delay={400}>
            <p className="text-lg text-white/70 leading-relaxed mb-10 max-w-2xl mx-auto">
              The goal: continue creating safe, inclusive and fun spaces for
              Marblehead children and future generations.
            </p>
          </Reveal>

          <Reveal delay={500}>
            <div className="flex flex-col items-center gap-4">
              <ExternalLinkButton
                href={links.gatchellDonation}
                variant="primary"
                className="text-xl px-10 py-5"
              >
                <Heart className="w-5 h-5" />
                Help Build Gatchell
              </ExternalLinkButton>
              <a
                href={links.familyFundAbout}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[#f3a06f] hover:text-[#e88f5a] transition-colors text-base"
              >
                Learn more at the Marblehead Family Fund
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 7. THE COFFEE */}
      <section className="bg-[#0f2238] py-20 sm:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <p className="text-[#f3a06f] text-xs sm:text-sm font-bold uppercase tracking-[0.25em] mb-4 text-center">
              What You’re Drinking
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-center mb-10">
              Roasted right here by Bond Coffee.
            </h2>
          </Reveal>

          <Reveal delay={200}>
            <div className="bg-[#132947] rounded-3xl border border-white/10 shadow-xl">
              <div className="grid sm:grid-cols-2 gap-0 items-stretch">
                <div className="relative min-h-[300px] sm:min-h-[520px] bg-[#f5eddf] flex items-center justify-center p-6">
                  <img
                    src="/MHD_Family_Fun copy 2.png"
                    alt="Marblehead Family Fund and Bond Coffee limited roast label"
                    className="w-full max-w-[260px] sm:max-w-[320px] rounded-xl shadow-2xl"
                  />
                </div>
                <div className="p-8 sm:p-10 space-y-5">
                  <div className="inline-flex items-center gap-2 bg-[#f3a06f]/10 border border-[#f3a06f]/30 rounded-full px-4 py-1.5">
                    <Coffee className="w-4 h-4 text-[#f3a06f]" />
                    <span className="text-[#f3a06f] text-sm font-semibold">
                      Limited Release
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl font-bold">
                    Marblehead Family Fund Roast
                  </h3>
                  <p className="text-white/70 leading-relaxed">
                    A local coffee for a local cause. Smooth and balanced, crafted
                    to bring people together — just like the spaces this roast
                    helps support.
                  </p>
                  <div className="grid grid-cols-3 gap-3 pt-2">
                    <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                      <p className="text-xs text-white/50 uppercase tracking-wide mb-1">
                        Price
                      </p>
                      <p className="text-sm font-medium text-white">$25</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                      <p className="text-xs text-white/50 uppercase tracking-wide mb-1">
                        Roast
                      </p>
                      <p className="text-sm font-medium text-white">Medium</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                      <p className="text-xs text-white/50 uppercase tracking-wide mb-1">
                        Size
                      </p>
                      <p className="text-sm font-medium text-white">10 oz</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {['Smooth', 'Balanced', 'Community'].map((note) => (
                      <span
                        key={note}
                        className="bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs text-white/70"
                      >
                        {note}
                      </span>
                    ))}
                  </div>
                  <a
                    href={links.buyRoast}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full bg-[#f3a06f] text-[#132947] px-6 py-4 rounded-full font-semibold text-lg hover:bg-[#e88f5a] transition-all duration-300 hover:scale-[1.02] shadow-lg shadow-[#f3a06f]/20"
                  >
                    <Coffee className="w-5 h-5" />
                    Buy This Roast — $25
                    <ArrowRight className="w-5 h-5" />
                  </a>
                  <p className="text-center text-sm text-white/50 pt-1">
                    Local delivery only
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={300}>
            <div className="text-center mt-8">
              <Link
                to={links.bondCoffee}
                className="inline-flex items-center gap-2 text-[#f3a06f] hover:text-[#e88f5a] transition-colors text-base font-medium"
              >
                Explore Bond Coffee
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 8. PASS IT ON */}
      <section className="bg-[#f5eddf] text-[#132947] py-20 sm:py-28 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <Reveal>
            <Share2 className="w-10 h-10 text-[#c97b4a] mx-auto mb-6" />
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold mb-4">
              Good coffee. Good town. Pass it on.
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-lg text-[#132947]/70 mb-10 max-w-lg mx-auto">
              Know someone who loves Marblehead? Share the story and help more
              people discover what the Family Fund is building.
            </p>
          </Reveal>

          <Reveal delay={300}>
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-2 bg-[#132947] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#1a3a5e] transition-all duration-300 hover:scale-105 shadow-lg"
            >
              <Share2 className="w-5 h-5" />
              Share This Story
            </button>
          </Reveal>

          <Reveal delay={400}>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <a
                href={`mailto:?subject=${encodeURIComponent(
                  'Coffee & Community | Bond Coffee × Marblehead Family Fund'
                )}&body=${encodeURIComponent(
                  'See how your Bond Coffee purchase supports the Marblehead Family Fund: ' +
                    window.location.href
                )}`}
                className="inline-flex items-center gap-2 bg-white border border-[#132947]/15 rounded-full px-5 py-2.5 text-sm font-medium text-[#132947] hover:bg-[#132947]/5 transition-colors"
              >
                <Mail className="w-4 h-4" /> Email
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                  window.location.href
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white border border-[#132947]/15 rounded-full px-5 py-2.5 text-sm font-medium text-[#132947] hover:bg-[#132947]/5 transition-colors"
              >
                <Facebook className="w-4 h-4" /> Facebook
              </a>
              <button
                onClick={() => {
                  navigator.clipboard
                    ?.writeText(window.location.href)
                    .then(() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    })
                    .catch(() => {});
                }}
                className="inline-flex items-center gap-2 bg-white border border-[#132947]/15 rounded-full px-5 py-2.5 text-sm font-medium text-[#132947] hover:bg-[#132947]/5 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-600" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" /> Copy link
                  </>
                )}
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 9. FINAL CTA */}
      <section className="relative py-24 sm:py-32 px-6 overflow-hidden bg-gradient-to-br from-[#0f2238] via-[#132947] to-[#1a3a5e]">
        <div className="absolute inset-0 opacity-30" aria-hidden="true">
          <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-[#f3a06f]/15 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-emerald-600/10 rounded-full blur-3xl" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#132947]/60 via-[#132947]/80 to-[#132947]" />

        <div className="relative max-w-2xl mx-auto text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold mb-6">
              Help build what’s next.
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-xl text-white/80 mb-10 max-w-lg mx-auto">
              Your coffee already contributed. Want to make an even bigger impact?
            </p>
          </Reveal>
          <Reveal delay={200}>
            <div className="flex flex-col items-center gap-4">
              <ExternalLinkButton
                href={links.gatchellDonation}
                variant="primary"
                className="text-xl px-10 py-5"
              >
                <Heart className="w-5 h-5" />
                Give for Gatchell
              </ExternalLinkButton>
              <a
                href={links.familyFund}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-white/70 hover:text-white transition-colors text-base"
              >
                Visit mhdfamilyfund.org
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 10. FOOTER */}
      <footer className="bg-[#0a1a2e] border-t border-white/10 py-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-serif text-xl font-bold text-white mb-2">
            Bond Coffee × Marblehead Family Fund
          </p>
          <p className="text-white/50 text-sm mb-8">
            Roasted locally. Giving locally.
          </p>

          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm">
            <a
              href={links.familyFund}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-white/70 hover:text-[#f3a06f] transition-colors"
            >
              Marblehead Family Fund
              <ExternalLink className="w-3 h-3 opacity-60" />
            </a>
            <a
              href={links.gatchellDonation}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-white/70 hover:text-[#f3a06f] transition-colors"
            >
              Give for Gatchell
              <ExternalLink className="w-3 h-3 opacity-60" />
            </a>
            <Link
              to="/"
              className="text-white/70 hover:text-[#f3a06f] transition-colors"
            >
              Bond Coffee
            </Link>
            <a
              href={links.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-white/70 hover:text-[#f3a06f] transition-colors"
            >
              Instagram
              <ExternalLink className="w-3 h-3 opacity-60" />
            </a>
          </div>

          <p className="text-white/30 text-xs mt-8">
            @marbleheadfamilyfund · Links to the Marblehead Family Fund open in a
            new tab and leave the Bond Coffee website.
          </p>
        </div>
      </footer>
    </div>
  );
}
